import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const REPORT_TITLE = {
  shift: 'SHIFT REPORT',
  bonus: 'BONUS REPORT',
  raffle: 'RAFFLE REPORT',
  points: 'POINTS REPORT',
  ticket: 'TICKET OUT REPORT',
};

// Wait for every <img> in the source element to finish loading before
// html2canvas snapshots the DOM. PdfBody already rewrites cross-origin
// image URLs to same-origin relative paths (via pdfSrc) so the CRA dev
// proxy can forward them — same behaviour as the on-screen UI's plain
// <img> tags. Hard cap so a 404/blocked image can't hang the export.
async function awaitImages(element) {
  const imgs = Array.from(element.querySelectorAll('img'));
  await Promise.all(
    imgs.map((img) => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise((resolve) => {
        const done = () => resolve();
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
        setTimeout(done, 5000);
      });
    })
  );
}

// Captures the given DOM element with html2canvas and wraps the image into
// a real PDF (multi-page if it overflows). Returns an ArrayBuffer suitable
// for Utils.handlePdfAction(buffer, 'download', filename).
export default async function generateSessionPdf({ element }) {
  if (!element) return null;

  await awaitImages(element);

  // scale=3 → ~280 DPI on an A4 page (194mm usable width × 3× = sharp text
  // without bloating the PDF the way scale=4 would).
  const canvas = await html2canvas(element, {
    scale: 3,
    useCORS: true,
    backgroundColor: '#ffffff',
    windowWidth: element.scrollWidth,
    imageTimeout: 0,
    logging: false,
  });

  // A4 portrait — 210mm × 297mm. 8mm margin on all sides ⇒ 194mm × 281mm
  // usable area. Slices are paginated automatically if content overflows.
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 8;
  const imgWidth = pageWidth - margin * 2;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  // JPEG at 0.85 quality cuts the embedded payload ~10x vs PNG with no
  // visible quality loss for the photo-heavy bonus/raffle reports. White
  // background already applied (#ffffff above) so JPEG's missing alpha
  // channel doesn't cause artifacts.
  const JPEG_QUALITY = 0.85;

  if (imgHeight <= pageHeight - margin * 2) {
    pdf.addImage(
      canvas.toDataURL('image/jpeg', JPEG_QUALITY),
      'JPEG',
      margin,
      margin,
      imgWidth,
      imgHeight,
      undefined,
      'FAST'
    );
  } else {
    // Image is taller than one page. Compute candidate break points from
    // [data-pdf-block] boundaries inside the source element so a page break
    // never falls inside an atomic block (audit row, KPI card, header).
    const pxPerMm = canvas.width / imgWidth;
    const usableHeightMm = pageHeight - margin * 2;
    const maxSlicePx = Math.floor(usableHeightMm * pxPerMm);

    // Collect block-top Y-coords (in canvas px) relative to the captured
    // root. Use the bounding rect minus the root's rect so it works even
    // when the source is positioned offscreen.
    const rootRect = element.getBoundingClientRect();
    const blockEls = element.querySelectorAll('[data-pdf-block]');
    const breakpoints = Array.from(blockEls).map((b) => {
      const r = b.getBoundingClientRect();
      return Math.round(
        (r.top - rootRect.top) * (canvas.height / element.scrollHeight)
      );
    });
    // Always allow breaking at the very end of the canvas.
    breakpoints.push(canvas.height);
    // Sort + dedupe ascending.
    const sortedBreaks = Array.from(new Set(breakpoints)).sort((a, b) => a - b);

    // Minimum slice height — never cut so close to the top that we make
    // pages of just a few mm of content.
    const minSlicePx = Math.floor(20 * pxPerMm);

    let cursor = 0;
    let firstPage = true;
    let safety = 0;
    while (cursor < canvas.height && safety++ < 200) {
      const maxEnd = Math.min(cursor + maxSlicePx, canvas.height);
      // Find the largest breakpoint in (cursor + minSlicePx, maxEnd].
      let breakAt = maxEnd;
      for (const bp of sortedBreaks) {
        if (bp > cursor + minSlicePx && bp <= maxEnd) breakAt = bp;
      }
      // If no breakpoint in range (e.g. a block taller than one page),
      // fall back to a flat cut at maxEnd. Better cut than infinite loop.
      const sliceHeight = breakAt - cursor;
      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = canvas.width;
      sliceCanvas.height = sliceHeight;
      const ctx = sliceCanvas.getContext('2d');
      ctx.drawImage(
        canvas,
        0,
        cursor,
        canvas.width,
        sliceHeight,
        0,
        0,
        canvas.width,
        sliceHeight
      );
      const sliceImgHeight = (sliceHeight * imgWidth) / canvas.width;
      if (!firstPage) pdf.addPage();
      pdf.addImage(
        sliceCanvas.toDataURL('image/jpeg', JPEG_QUALITY),
        'JPEG',
        margin,
        margin,
        imgWidth,
        sliceImgHeight,
        undefined,
        'FAST'
      );
      cursor = breakAt;
      firstPage = false;
    }
  }

  return pdf.output('arraybuffer');
}

export function suggestedFilename({ type, session, locationName }) {
  const safe = (s) =>
    String(s || '')
      .replace(/[^\w-]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  const parts = [
    REPORT_TITLE[type] || 'Report',
    safe(locationName),
    safe(session?.employee),
    session?.id ? `#${safe(session.id)}` : null,
  ].filter(Boolean);
  return `${parts.join('_')}.pdf`;
}
